import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {ActionMethod, ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  TransitionSource,
  TaskSchemaNodeSection,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {
  TaskSchemaNode,
  FlowchartNode,
  isInteractiveTaskNode,
  getInteractiveTaskNodeDatas,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {getTransitionSourceByNodeIdPairKey} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/TransitionSourceUtils';
import {getTransitionSourceForNodePairWithDefault} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/types/transition/transitionLookupUtils';
import {useTransitionsContext} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/types/transition/TransitionsContext';
import {getExecutionNodeTypeIdentifierForNode} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useFlowchartState/useFlowchartStateAnalytics';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {ORANGE_DARK} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';

import {findAllUpstreamMappableNodeIds} from '../TraverseFlowchart';
import {NodeDetailsSidepanelContext} from './NodeDetailsSidepanelContext';

function getOptionTextForTransitionSource(transitionSource: TransitionSource): string {
  switch (transitionSource) {
    case TransitionSource.OUTPUTS:
      return 'Outputs';
    case TransitionSource.INPUTS:
      return 'Tasks';
    default:
      throw new Error(`Unexpected transition source: ${transitionSource}`);
  }
}

export interface FlowchartSettingsTabContentProps {
  locked?: boolean;
}

interface SettingsContentProps {
  selectedNodeName: string;
  selectedNodeIsOutputNode: boolean;
  connectedNodes: Array<TaskSchemaNode>;
  transitionSources: Record<string, TransitionSource>;
  handleTransitionSourceSelect: (upstreamMappableNodeIdToUpdate: string, value: TransitionSource) => void;
  locked?: boolean;
}

function EmptySettingsContent(props: {selectedNodeName: string}): JSX.Element {
  const {selectedNodeName} = props;
  return (
    <div className={css(styles.emptyStateContainer)}>
      <div className={css(styles.flowchartSettingDescription)}>
        There are no mappings to configure for
        <span className={css(styles.flowchartSettingDescriptionBold)}>{selectedNodeName}</span>.
      </div>
    </div>
  );
}

function SettingsContent(props: SettingsContentProps): JSX.Element {
  const {
    selectedNodeName,
    selectedNodeIsOutputNode,
    connectedNodes,
    transitionSources,
    handleTransitionSourceSelect,
    locked,
  } = props;

  return (
    <>
      <div className={css(styles.flowchartSettingDescription)}>
        <span>Define the task creation & field population behavior for each transition into&nbsp;</span>
        <Chip text={selectedNodeName} size={ChipSize.MINI} />
      </div>
      <Table
        numRows={connectedNodes.length}
        calculateInView={false}
        fitContent={false}
        frozenHeader={false}
        getRowKey={(rowIndex) => connectedNodes[rowIndex].id}
        includeNegativeMargins={false}
        isCompact={false}
        overflowVisible={false}
        tableLayout="fixed"
      >
        <Column
          header={<Cell className={css(styles.fromColumn)}>From</Cell>}
          renderCell={(rowIdx: number) => {
            const node = connectedNodes[rowIdx];
            return (
              <Cell className={css(styles.fromColumn, styles.wrapCellText)}>
                <Chip text={node.data.nodeName} size={ChipSize.MINI} />
              </Cell>
            );
          }}
        />
        <Column
          header="Behavior"
          renderCell={(rowIdx: number) => {
            const node = connectedNodes[rowIdx];
            const isRootNode =
              node.data.nodeDetailsType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS;
            const hasOutputSection = node.data.sections.some(
              (section) => section.sectionName === TaskSchemaNodeSection.OUTPUTS
            );
            const availableTransitionSources = hasOutputSection
              ? Object.values(TransitionSource)
              : [TransitionSource.INPUTS];

            return (
              <Cell className={css(styles.tableCell, styles.wrapCellText)}>
                {isRootNode ? (
                  <div>
                    <span>The flowchart's tasks will be used to create tasks &amp; populate fields.</span>
                  </div>
                ) : (
                  <>
                    <span>Use &nbsp;</span>
                    <div className={css(styles.fieldSourceDropdownWrapper)}>
                      <SelectDropdown
                        dataTestAttrs={{
                          component: 'TransitionSourceDropdown',
                          element: 'root',
                          key: node.data.nodeName,
                        }}
                        menu={
                          <Menu
                            height={Menu.HEIGHTS.MEDIUM}
                            width={Menu.WIDTHS.LARGE}
                            value={transitionSources[node.id]}
                          >
                            {availableTransitionSources.map((transitionSource) => (
                              <Option
                                key={transitionSource}
                                value={transitionSource}
                                onSelect={(value: TransitionSource) => {
                                  handleTransitionSourceSelect(node.id, value);
                                }}
                                isDisabled={locked}
                              >
                                {getOptionTextForTransitionSource(transitionSource)}
                              </Option>
                            ))}
                          </Menu>
                        }
                        displayName={getOptionTextForTransitionSource(transitionSources[node.id])}
                      />
                    </div>
                    {selectedNodeIsOutputNode ? (
                      <span>&nbsp;to populate output fields on this flowchart</span>
                    ) : (
                      <span>&nbsp;to create tasks &amp; populate fields</span>
                    )}
                    {transitionSources[node.id] === TransitionSource.INPUTS && (
                      <div className={css(styles.warningMessage)}>
                        <BenchlingIcon icon="warning" className={css(styles.warningIcon)} />
                        <span className={css(styles.secondaryText)}>
                          Limits field mappings to input fields from the previous step
                        </span>
                      </div>
                    )}
                  </>
                )}
              </Cell>
            );
          }}
        />
      </Table>
    </>
  );
}

function areAllNodesInteractiveTaskNodes(nodes: Array<FlowchartNode>): nodes is Array<TaskSchemaNode> {
  return nodes.every((node) => isInteractiveTaskNode(node));
}

export default function FlowchartSettingsPane(props: FlowchartSettingsTabContentProps): JSX.Element {
  const {selectedNode, interactiveFlowchart, onChangeTransitionSource, flowchartEditorConfigurator} =
    NodeDetailsSidepanelContext.useContext();
  const {transitionsById, ptdvPairToTransitionIdIndex} = useTransitionsContext();

  const {upstreamMappableNodeIds, transitionSources} = useMemo(() => {
    const upstreamMappableNodeIds = findAllUpstreamMappableNodeIds(interactiveFlowchart, selectedNode.id);
    const taskNodes = getInteractiveTaskNodeDatas(interactiveFlowchart.nodes);
    const transitionSources: Record<string, TransitionSource> = {};
    for (const sourceNodeId of upstreamMappableNodeIds) {
      transitionSources[sourceNodeId] = getTransitionSourceForNodePairWithDefault({
        sourceNodeId,
        targetNodeId: selectedNode.id,
        nodes: taskNodes,
        transitionsById,
        ptdvPairToTransitionIdIndex,
        nodesForDefault: taskNodes,
      });
    }
    return {upstreamMappableNodeIds, transitionSources};
  }, [interactiveFlowchart, selectedNode.id, transitionsById, ptdvPairToTransitionIdIndex]);

  const {nodes} = interactiveFlowchart;
  const connectedNodes = Object.values(nodes).filter((node) => upstreamMappableNodeIds.includes(node.id));
  assert(areAllNodesInteractiveTaskNodes(connectedNodes), 'Expected all connected nodes to be task nodes');

  const handleTransitionSourceSelect = (
    upstreamMappableNodeIdToUpdate: string,
    value: TransitionSource
  ): void => {
    const currentTransitionSource = transitionSources[upstreamMappableNodeIdToUpdate];
    const nodeIdPair = getTransitionSourceByNodeIdPairKey(upstreamMappableNodeIdToUpdate, selectedNode.id);

    if (currentTransitionSource !== value) {
      analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.TRANSITION_SOURCE_CHANGED, {
        oldValue: currentTransitionSource,
        newValue: value,
        flowchartType: flowchartEditorConfigurator.flowchartTypeName,
        actionMethod: ActionMethod.BUTTON_CLICK,
        actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
        nodeType: getExecutionNodeTypeIdentifierForNode(selectedNode),
      });
      onChangeTransitionSource(nodeIdPair, value);
    }
  };

  return (
    <div key={selectedNode.id}>
      <div className={css(styles.settingsGroup)}>
        <div className={css(styles.flowchartSettingTitle)}>Transition behavior</div>
        {connectedNodes.length === 0 ? (
          <EmptySettingsContent selectedNodeName={selectedNode.data.nodeName} />
        ) : (
          <SettingsContent
            selectedNodeName={selectedNode.data.nodeName}
            selectedNodeIsOutputNode={
              isInteractiveTaskNode(selectedNode) &&
              selectedNode.data.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
            }
            connectedNodes={connectedNodes}
            transitionSources={transitionSources}
            handleTransitionSourceSelect={handleTransitionSourceSelect}
            locked={props.locked}
          />
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  settingsGroup: {},
  flowchartSettingTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: Color.GRAY_900,
    marginBottom: '4px',
  },
  flowchartSettingDescription: {
    fontSize: '14px',
    fontWeight: 400,
    color: Color.GRAY_900,
    flexWrap: 'nowrap',
    marginBottom: '10px',
  },
  warningMessage: {
    marginTop: '4px',
  },
  flowchartSettingDescriptionBold: {
    fontWeight: 600,
    paddingLeft: '4px',
  },
  fromColumn: {
    width: '33%',
  },
  wrapCellText: {
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
  },
  tableCell: {
    wordBreak: 'break-word',
  },
  primaryText: {
    color: Color.GRAY_900,
  },
  secondaryText: {
    color: Color.GRAY_600,
  },
  warningIcon: {
    paddingRight: 4,
    color: ORANGE_DARK,
  },
  emptyStateContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  fieldSourceDropdownWrapper: {
    display: 'inline-block',
  },
});
